import { Button, Popover, Select, Switch } from '../primitives';
import type { SelectOption } from '../primitives';
import {
  filterBadgeCount,
  PERSONAL_TODOS_DEFAULT_STATE,
  type PersonalTodosDueFilter,
  type PersonalTodosListState,
  type PersonalTodosPriorityFilter,
  type PersonalTodosStatusFilter
} from '../../lib/models/personalTodos';

const STATUS_OPTIONS: SelectOption[] = [
  { value: 'open', label: 'Open' },
  { value: 'done', label: 'Done' },
  { value: 'all', label: 'All' }
];

const PRIORITY_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'All' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' }
];

const DUE_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'All dates' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'today', label: 'Due today' },
  { value: 'week', label: 'Due this week' },
  { value: 'none', label: 'No due date' }
];

export interface PersonalTodosFilterMenuProps {
  state: PersonalTodosListState;
  onChange: (next: PersonalTodosListState) => void;
  testIdPrefix?: string;
}

export function PersonalTodosFilterMenu({
  state,
  onChange,
  testIdPrefix = 'pt'
}: PersonalTodosFilterMenuProps) {
  const badge = filterBadgeCount(state);

  const patch = (partial: Partial<PersonalTodosListState>) => {
    onChange({ ...state, ...partial });
  };

  return (
    <Popover
      label="Filter todos"
      align="start"
      data-testid={`${testIdPrefix}-filter-popover`}
      trigger={(api) => (
        <button
          type="button"
          className="pn-btn pn-btn-sm"
          data-testid={`${testIdPrefix}-filter-trigger`}
          {...api}
        >
          Filter{badge > 0 ? ` (${badge})` : ''} ▾
        </button>
      )}
    >
      {() => (
        <div className="pt-filter-pop" data-testid={`${testIdPrefix}-filter-body`}>
          <Select
            id={`${testIdPrefix}-f-status`}
            label="Status"
            options={STATUS_OPTIONS}
            value={state.status}
            onChange={(e) =>
              patch({ status: e.currentTarget.value as PersonalTodosStatusFilter })
            }
            data-testid={`${testIdPrefix}-f-status`}
          />
          <Select
            id={`${testIdPrefix}-f-priority`}
            label="Priority"
            options={PRIORITY_OPTIONS}
            value={state.priority}
            onChange={(e) =>
              patch({
                priority: e.currentTarget.value as PersonalTodosPriorityFilter
              })
            }
            data-testid={`${testIdPrefix}-f-priority`}
          />
          <Select
            id={`${testIdPrefix}-f-due`}
            label="Date range"
            options={DUE_OPTIONS}
            value={state.due}
            onChange={(e) =>
              patch({ due: e.currentTarget.value as PersonalTodosDueFilter })
            }
            data-testid={`${testIdPrefix}-f-due`}
          />
          <div className="pt-filter-toggle-row">
            <Switch
              label="Include archived"
              checked={state.includeArchived}
              onChange={(e) => patch({ includeArchived: e.currentTarget.checked })}
              data-testid={`${testIdPrefix}-include-archived`}
            />
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="pt-filter-clear"
            data-testid={`${testIdPrefix}-filter-clear`}
            onClick={() => onChange({ ...PERSONAL_TODOS_DEFAULT_STATE })}
          >
            Clear all
          </Button>
        </div>
      )}
    </Popover>
  );
}
